import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Sun, Moon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

const Navigation = ({ onRequestDemo }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const isActive = (path) => location.pathname === path;

  const solutions = [
    { name: 'Security & Surveillance', path: '/solutions/security' },
    { name: 'Industry 4.0', path: '/solutions/industry-4' },
    { name: 'Agriculture', path: '/solutions/agriculture' },
    { name: 'Healthcare (WIP)', path: '/solutions/healthcare' }
  ];

  return (
    <>
      <nav className="nav-header">
        <div className="max-w-7xl mx-auto w-full flex items-center justify-between px-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 text-[var(--text-primary)] font-semibold hover:opacity-80 transition-opacity">
            <img 
              src="https://customer-assets.emergentagent.com/job_smart-site-intel/artifacts/iqxongg8_Logo%20-%20Fern%20Tree.png" 
              alt="Ferntree Logo" 
              className="h-8 w-auto"
            />
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>Ferntree</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                isActive('/') ? 'bg-[var(--accent-wash)] text-[var(--accent-text)]' : 'text-[var(--text-muted)] hover:bg-[rgba(0,0,0,0.05)] hover:text-[var(--text-primary)]'
              }`}
            >
              Home
            </Link>
            
            {/* Solutions Dropdown */}
            <div className="relative"
              onMouseEnter={() => setIsSolutionsOpen(true)}
              onMouseLeave={() => setIsSolutionsOpen(false)}
            >
              <button
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${
                  location.pathname.startsWith('/solutions') ? 'bg-[var(--accent-wash)] text-[var(--accent-text)]' : 'text-[var(--text-muted)] hover:bg-[rgba(0,0,0,0.05)] hover:text-[var(--text-primary)]'
                }`}
              >
                Solutions
                <ChevronDown className="w-4 h-4" />
              </button>
              <AnimatePresence>
                {isSolutionsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-56 bg-[var(--bg-card)] border border-[var(--border-light)] rounded-xl shadow-lg overflow-hidden"
                  >
                    {solutions.map((solution, idx) => (
                      <Link
                        key={idx}
                        to={solution.path}
                        className="block px-4 py-3 text-sm text-[var(--text-body)] hover:bg-[var(--bg-section)] transition-colors"
                      >
                        {solution.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/services"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                isActive('/services') ? 'bg-[var(--accent-wash)] text-[var(--accent-text)]' : 'text-[var(--text-muted)] hover:bg-[rgba(0,0,0,0.05)] hover:text-[var(--text-primary)]'
              }`}
            >
              Services
            </Link>
            <Link
              to="/platform"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                isActive('/platform') ? 'bg-[var(--accent-wash)] text-[var(--accent-text)]' : 'text-[var(--text-muted)] hover:bg-[rgba(0,0,0,0.05)] hover:text-[var(--text-primary)]'
              }`}
            >
              Platform
            </Link>
            <Link
              to="/industries"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                isActive('/industries') ? 'bg-[var(--accent-wash)] text-[var(--accent-text)]' : 'text-[var(--text-muted)] hover:bg-[rgba(0,0,0,0.05)] hover:text-[var(--text-primary)]'
              }`}
            >
              Industries
            </Link>
            <Link
              to="/about"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                isActive('/about') ? 'bg-[var(--accent-wash)] text-[var(--accent-text)]' : 'text-[var(--text-muted)] hover:bg-[rgba(0,0,0,0.05)] hover:text-[var(--text-primary)]'
              }`}
            >
              About
            </Link>
            <Link
              to="/contact"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                isActive('/contact') ? 'bg-[var(--accent-wash)] text-[var(--accent-text)]' : 'text-[var(--text-muted)] hover:bg-[rgba(0,0,0,0.05)] hover:text-[var(--text-primary)]'
              }`}
            >
              Contact
            </Link>
          </div>

          {/* Right side - Theme Toggle & CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-[rgba(0,0,0,0.05)] transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon className="w-5 h-5 text-[var(--text-muted)]" /> : <Sun className="w-5 h-5 text-[var(--text-muted)]" />}
            </button>
            <button onClick={onRequestDemo} className="btn-primary text-sm px-5 py-2">
              Request a Demo
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-[var(--text-primary)]"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="fixed top-[4.5rem] left-0 right-0 bg-[var(--bg-card)] border-b border-[var(--border-light)] shadow-lg lg:hidden z-40 overflow-hidden"
          >
            <div className="p-4 space-y-2">
              <Link to="/" onClick={() => setIsMenuOpen(false)} className="block px-4 py-3 rounded-lg text-[var(--text-body)] hover:bg-[var(--bg-section)] transition-colors">
                Home
              </Link>
              <div>
                <button
                  onClick={() => setIsSolutionsOpen(!isSolutionsOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-[var(--text-body)] hover:bg-[var(--bg-section)] transition-colors"
                >
                  Solutions
                  <ChevronDown className={`w-4 h-4 transition-transform ${isSolutionsOpen ? 'rotate-180' : ''}`} />
                </button>
                {isSolutionsOpen && (
                  <div className="ml-4 mt-2 space-y-2">
                    {solutions.map((solution, idx) => (
                      <Link
                        key={idx}
                        to={solution.path}
                        onClick={() => setIsMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-body)] transition-colors"
                      >
                        {solution.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <Link to="/services" onClick={() => setIsMenuOpen(false)} className="block px-4 py-3 rounded-lg text-[var(--text-body)] hover:bg-[var(--bg-section)] transition-colors">
                Services
              </Link>
              <Link to="/platform" onClick={() => setIsMenuOpen(false)} className="block px-4 py-3 rounded-lg text-[var(--text-body)] hover:bg-[var(--bg-section)] transition-colors">
                Platform
              </Link>
              <Link to="/industries" onClick={() => setIsMenuOpen(false)} className="block px-4 py-3 rounded-lg text-[var(--text-body)] hover:bg-[var(--bg-section)] transition-colors">
                Industries
              </Link>
              <Link to="/about" onClick={() => setIsMenuOpen(false)} className="block px-4 py-3 rounded-lg text-[var(--text-body)] hover:bg-[var(--bg-section)] transition-colors">
                About
              </Link>
              <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="block px-4 py-3 rounded-lg text-[var(--text-body)] hover:bg-[var(--bg-section)] transition-colors">
                Contact
              </Link>
              <div className="pt-2 space-y-2">
                <button
                  onClick={() => { toggleTheme(); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-[var(--border-light)] text-[var(--text-body)] hover:bg-[var(--bg-section)] transition-colors"
                >
                  {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                  {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                </button>
                <button onClick={() => { onRequestDemo(); setIsMenuOpen(false); }} className="btn-primary w-full">
                  Request a Demo
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;